// src/pages/admin/AdminQuartiers.js
// Gestion des quartiers connectée à l'API - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { CITIES } from '../../data/quartiers';
import apiClient from '../../api/client';

const AdminQuartiers = () => {
  const navigate = useNavigate();
  const [quartiers, setQuartiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuartier, setNewQuartier] = useState({ name: '', city: 'Douala', lat: '', lng: '' });

  const loadQuartiers = async () => {
    setLoading(true);
    try {
      const result = await apiClient.get('/endpoints/quartiers.php?action=list');
      setQuartiers(result.quartiers || []);
    } catch (err) {
      setQuartiers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuartiers();
  }, []);

  const handleAdd = async () => {
    if (!newQuartier.name.trim()) {
      alert('Le nom du quartier est requis');
      return;
    }
    try {
      await apiClient.post('/endpoints/quartiers.php?action=create', newQuartier);
      setNewQuartier({ name: '', city: 'Douala', lat: '', lng: '' });
      setShowAddForm(false);
      loadQuartiers();
    } catch (err) {
      alert("Erreur lors de l'ajout du quartier");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce quartier ?')) return;
    try {
      await apiClient.delete(`/endpoints/quartiers.php?action=delete&id=${id}`);
      setQuartiers((prev) => prev.filter((q) => q.id !== id));
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF', flex: 1 },
    addButton: { padding: '8px 16px', backgroundColor: '#FFD700', border: 'none', borderRadius: '8px', color: '#0A0E1A', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    addForm: { margin: '16px 20px', padding: '16px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', display: 'flex', flexDirection: 'column', gap: '12px' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    row: { display: 'flex', gap: '10px' },
    label: { fontSize: '12px', fontWeight: '600', color: '#A0AEC0' },
    input: { padding: '10px 12px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '14px', outline: 'none', width: '100%' },
    select: { padding: '10px 12px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '14px', outline: 'none', width: '100%' },
    formButtons: { display: 'flex', gap: '10px' },
    saveBtn: { flex: 1, padding: '10px', backgroundColor: '#FFD700', border: 'none', borderRadius: '8px', color: '#0A0E1A', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    cancelBtn: { flex: 1, padding: '10px', backgroundColor: '#162033', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#A0AEC0', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    list: { display: 'flex', flexDirection: 'column', gap: '8px', padding: '0 20px 40px' },
    cityGroup: { marginTop: '12px' },
    cityTitle: { fontSize: '13px', fontWeight: '700', color: '#FFD700', padding: '8px 0', textTransform: 'uppercase' },
    card: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', backgroundColor: '#162033', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.1)', marginBottom: '8px' },
    quartierName: { fontSize: '14px', color: '#FFFFFF', fontWeight: '500' },
    deleteBtn: { padding: '6px 12px', backgroundColor: 'rgba(245, 101, 101, 0.15)', color: '#F56565', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    empty: { textAlign: 'center', padding: '48px', color: '#A0AEC0', fontSize: '14px' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Quartiers | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
        <div style={styles.title}>Quartiers</div>
        <button style={styles.addButton} onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕' : '+ Ajouter'}
        </button>
      </div>

      {showAddForm && (
        <div style={styles.addForm}>
          <div style={styles.field}>
            <label style={styles.label}>Nom du quartier</label>
            <input type="text" style={styles.input} value={newQuartier.name} onChange={(e) => setNewQuartier({ ...newQuartier, name: e.target.value })} placeholder="Ex: Makepe" />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Ville</label>
            <select style={styles.select} value={newQuartier.city} onChange={(e) => setNewQuartier({ ...newQuartier, city: e.target.value })}>
              {CITIES.map((city) => (<option key={city} value={city}>{city}</option>))}
            </select>
          </div>
          <div style={styles.row}>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>Latitude</label>
              <input type="text" style={styles.input} value={newQuartier.lat} onChange={(e) => setNewQuartier({ ...newQuartier, lat: e.target.value })} placeholder="4.05" />
            </div>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>Longitude</label>
              <input type="text" style={styles.input} value={newQuartier.lng} onChange={(e) => setNewQuartier({ ...newQuartier, lng: e.target.value })} placeholder="9.76" />
            </div>
          </div>
          <div style={styles.formButtons}>
            <button style={styles.cancelBtn} onClick={() => setShowAddForm(false)}>Annuler</button>
            <button style={styles.saveBtn} onClick={handleAdd}>Ajouter</button>
          </div>
        </div>
      )}

      <div style={styles.list}>
        {loading ? (
          <div style={styles.empty}>Chargement...</div>
        ) : (
          CITIES.map((city) => {
            const cityQuartiers = quartiers.filter((q) => q.city === city);
            if (cityQuartiers.length === 0) return null;
            return (
              <div key={city} style={styles.cityGroup}>
                <div style={styles.cityTitle}>{city} ({cityQuartiers.length})</div>
                {cityQuartiers.map((quartier) => (
                  <div key={quartier.id} style={styles.card}>
                    <span style={styles.quartierName}>📍 {quartier.name}</span>
                    <button style={styles.deleteBtn} onClick={() => handleDelete(quartier.id)}>Suppr.</button>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminQuartiers;